import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import ArtworkCard from "@/components/ArtworkCard";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/MoneyInput";
import { Button } from "@/components/ui/button";
import { Search, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState(["All"]);
  const [category, setCategory] = useState("All");
  const [tier, setTier] = useState("All");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [appliedPrice, setAppliedPrice] = useState({ min: undefined, max: undefined });
  const sentinel = useRef(null);
  const TIERS = ["All", "Basic", "Classic", "Premium", "Platinum"];

  useEffect(() => {
    api.get("/gallery/categories").then((r) => setCategories(["All", ...r.data.categories]));
  }, []);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery", { params: {
        page: p, limit: 20, category, tier: tier === "All" ? undefined : tier, q: search || undefined,
        min_price: appliedPrice.min, max_price: appliedPrice.max,
      } });
      setItems(data.items);
      setHasMore(data.has_more);
      setTotal(data.total);
      setPage(p);
    } finally {
      setLoading(false);
    }
  }, [category, tier, search, appliedPrice]);

  // reset on filter change
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, tier, search, appliedPrice]);

  const gotoPage = (p) => { window.scrollTo({ top: 0, behavior: "smooth" }); load(p); };

  const applyPrice = (e) => {
    e.preventDefault();
    setAppliedPrice({
      min: priceMin !== "" ? Number(priceMin) : undefined,
      max: priceMax !== "" ? Number(priceMax) : undefined,
    });
  };
  const clearPrice = () => { setPriceMin(""); setPriceMax(""); setAppliedPrice({ min: undefined, max: undefined }); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold">Virtual Gallery</h1>
          <p className="text-sm text-muted-foreground mt-1">{total.toLocaleString()} public-domain masterpieces to collect.</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setSearch(q); }} className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="gallery-search" placeholder="Search artworks…" className="h-11 rounded-full pl-10" />
        </form>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {categories.map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c}`}
            className={`px-4 h-9 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${category === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="tier-filter">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground mr-1 shrink-0">Tier</span>
        {TIERS.map((t) => (
          <button key={t} onClick={() => setTier(t)} data-testid={`tier-${t}`}
            className={`px-4 h-8 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${tier === t ? "bg-gold text-white border-gold" : "border-border hover:bg-secondary"}`}>
            {t}
          </button>
        ))}
      </div>

      <form onSubmit={applyPrice} className="flex flex-wrap items-end gap-2" data-testid="price-range-filter">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground w-full sm:w-auto">Price range (₦)</span>
        <MoneyInput value={priceMin} onValueChange={setPriceMin} data-testid="price-min" placeholder="Min (₦3,000)" className="h-10 rounded-full w-32" />
        <span className="text-muted-foreground">–</span>
        <MoneyInput value={priceMax} onValueChange={setPriceMax} data-testid="price-max" placeholder="Max (₦1,500,000)" className="h-10 rounded-full w-40" />
        <Button type="submit" data-testid="price-apply" className="h-10 rounded-full navy-gradient text-white font-semibold px-5">Apply</Button>
        {(appliedPrice.min !== undefined || appliedPrice.max !== undefined) && (
          <Button type="button" variant="outline" onClick={clearPrice} data-testid="price-clear" className="h-10 rounded-full px-4">Clear</Button>
        )}
      </form>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
        {items.map((art, i) => <ArtworkCard key={art.id} art={art} index={i} />)}
        {loading && items.length === 0 && Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />
        ))}
      </div>

      <div className="flex items-center justify-center gap-3 py-4" data-testid="gallery-pagination">
        {loading && <Loader2 className="h-5 w-5 animate-spin text-gold" />}
        {!loading && items.length === 0 && <p className="text-sm text-muted-foreground">No artworks found.</p>}
        {!loading && items.length > 0 && (
          <>
            <Button variant="outline" className="rounded-full" disabled={page <= 1} onClick={() => gotoPage(page - 1)} data-testid="gallery-prev">Previous</Button>
            <span className="text-sm text-muted-foreground" data-testid="gallery-page-indicator">Page {page} of {Math.max(1, Math.ceil(total / 20))}</span>
            <Button variant="outline" className="rounded-full" disabled={!hasMore} onClick={() => gotoPage(page + 1)} data-testid="gallery-next">Next</Button>
          </>
        )}
      </div>
    </div>
  );
}
